<template>
  <div class="visualization-form-container">
    <v-form ref="form" @submit.prevent="submit">
      <!-- Header -->
      <header class="form-header">
        <div class="header-content">
          <div class="header-icon">
            <span class="material-symbols-outlined">monitoring</span>
          </div>
          <div class="header-info">
            <h1>{{ visualization.id ? 'Modifier la visualisation' : 'Nouvelle visualisation' }}</h1>
            <p v-if="visualization.name">{{ visualization.name }}</p>
            <p v-else class="placeholder-text">Configuration du graphique</p>
          </div>
        </div>
      </header>

      <!-- Form Body -->
      <div class="form-body">
        <!-- Basic Info Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">edit</span>
            <h2>Informations générales</h2>
          </div>

          <div class="field-group">
            <label class="field-label required">Intitulé de la visualisation</label>
            <input
                type="text"
                v-model="visualization.name"
                class="form-input"
                placeholder="Ex: Température chai 1..."
            />
          </div>

          <div class="field-group">
            <label class="field-label">Type de graphique global</label>
            <v-select
                v-model="visualization.component"
                :items="globalChartTypes"
                item-text="text"
                item-value="value"
                solo
                flat
                hide-details
                dense
                class="custom-autocomplete"
            ></v-select>
            <span class="field-hint">Définit le rendu global du graphique (indépendant des types d’axes)</span>
          </div>
        </section>

        <!-- Axes Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">ssid_chart</span>
            <h2>Axes & Mesures</h2>
            <span class="section-badge" v-if="visualization.axes && visualization.axes.axes">
              {{ visualization.axes.axes.length }} axe{{ visualization.axes.axes.length !== 1 ? 's' : '' }}
            </span>
          </div>

          <VisualizationAxes
              :axes="visualization.axes.axes"
              :mesures="availableMesures"
              :childIdsToName="childIdsToName"
              :site="site"
              @added="handleAddedAxe"
          />
        </section>

        <!-- Events Section -->
        <section class="form-section">
          <div class="section-header">
            <span class="material-symbols-outlined">event</span>
            <h2>Événements</h2>
          </div>

          <div class="field-group">
            <label class="field-label">Événements à afficher</label>
            <v-autocomplete
                v-model="visualization.events"
                :items="events"
                item-value="id"
                item-text="name"
                multiple
                chips
                small-chips
                deletable-chips
                solo
                flat
                hide-details
                dense
                placeholder="Sélectionner des événements..."
                class="custom-autocomplete"
            ></v-autocomplete>
            <span class="field-hint">Les événements sélectionnés seront affichés sur le graphique</span>
          </div>
        </section>

        <!-- Annotation Zones Section -->
        <section class="form-section annotations-section">
          <div class="section-header">
            <span class="material-symbols-outlined">layers</span>
            <h2>Zones d'annotation</h2>
            <div class="section-actions">
              <span
                  class="ratio-badge"
                  :class="{ valid: totalRatio === 100, warning: totalRatio !== 100 }"
              >
                <span class="material-symbols-outlined">{{ totalRatio === 100 ? 'check_circle' : 'warning' }}</span>
                {{ totalRatio.toFixed(1) }}%
              </span>
              <button type="button" class="btn btn-sm btn-primary" @click="addAnnotationZone">
                <span class="material-symbols-outlined">add</span>
                Ajouter
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div class="empty-annotations" v-if="!hasAnnotationZones">
            <span class="material-symbols-outlined">layers</span>
            <h4>Aucune zone d'annotation</h4>
            <p>Ajoutez des zones pour diviser visuellement votre graphique</p>
            <button type="button" class="btn btn-secondary" @click="addAnnotationZone">
              <span class="material-symbols-outlined">add</span>
              Créer une zone
            </button>
          </div>

          <!-- Annotations List -->
          <div class="annotations-list" v-else>
            <!-- Zones -->
            <div
                class="annotation-card"
                v-for="(zone, index) in annotationZones"
                :key="index"
            >
              <!-- Drag Handle & Order -->
              <div class="annotation-order">
                <div class="order-controls">
                  <button
                      type="button"
                      class="order-btn"
                      @click="moveZoneUp(index)"
                      :disabled="index === 0"
                      title="Monter"
                  >
                    <span class="material-symbols-outlined">keyboard_arrow_up</span>
                  </button>
                  <span class="order-number">{{ index + 1 }}</span>
                  <button
                      type="button"
                      class="order-btn"
                      @click="moveZoneDown(index)"
                      :disabled="index === annotationZones.length - 1"
                      title="Descendre"
                  >
                    <span class="material-symbols-outlined">keyboard_arrow_down</span>
                  </button>
                </div>
              </div>

              <!-- Zone Content -->
              <div class="annotation-content">
                <!-- Color Picker -->
                <div class="color-field">
                  <v-menu :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="color-picker-trigger" v-bind="attrs" v-on="on">
                        <div
                            class="color-swatch"
                            :style="{ backgroundColor: zone.color }"
                        ></div>
                        <span class="color-hex">{{ zone.color }}</span>
                        <span class="material-symbols-outlined">palette</span>
                      </div>
                    </template>
                    <div class="color-picker-popup">
                      <v-color-picker
                          v-model="zone.color"
                          mode="hexa"
                          show-swatches
                          swatches-max-height="150"
                          hide-canvas
                      ></v-color-picker>
                    </div>
                  </v-menu>
                </div>

                <!-- Text Input -->
                <div class="text-field">
                  <input
                      type="text"
                      v-model="zone.text"
                      class="form-input"
                      placeholder="Nom de la zone..."
                  />
                </div>

                <!-- Ratio Input -->
                <div class="ratio-field">
                  <input
                      type="number"
                      :value="zone.ratio"
                      @input="updateRatio(index, $event.target.value)"
                      class="form-input ratio-input"
                      min="1"
                      max="100"
                      step="0.5"
                  />
                  <span class="ratio-suffix">%</span>
                </div>

                <!-- Preview Bar -->
                <div class="ratio-preview-bar">
                  <div
                      class="preview-fill"
                      :style="{
                      width: zone.ratio + '%',
                      backgroundColor: zone.color
                    }"
                  ></div>
                </div>
              </div>

              <!-- Delete Button -->
              <button
                  type="button"
                  class="annotation-delete"
                  @click="removeAnnotationZone(index)"
                  title="Supprimer"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>

            <!-- Visual Preview -->
            <div class="annotations-preview">
              <div class="preview-label">
                <span class="material-symbols-outlined">preview</span>
                Aperçu
              </div>
              <div class="preview-chart">
                <div
                    class="preview-zone"
                    v-for="(zone, index) in annotationZones"
                    :key="'preview-' + index"
                    :style="{
                    height: zone.ratio + '%',
                    backgroundColor: hexToRgba(zone.color, 0.3),
                    borderLeftColor: zone.color
                  }"
                >
                  <span class="preview-zone-text">{{ zone.text }}</span>
                  <span class="preview-zone-ratio">{{ (parseFloat(zone.ratio) || 0).toFixed(1) }}%</span>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="annotations-actions">
              <button type="button" class="btn btn-ghost" @click="equalizeRatios">
                <span class="material-symbols-outlined">sync</span>
                Égaliser
              </button>
              <button type="button" class="btn btn-ghost" @click="normalizeRatios">
                <span class="material-symbols-outlined">check</span>
                Normaliser à 100%
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <footer class="form-footer">
        <button type="button" class="btn btn-ghost" @click="cancel">
          Annuler
        </button>
        <button type="submit" class="btn btn-primary">
          <span class="material-symbols-outlined">save</span>
          Enregistrer
        </button>
      </footer>
    </v-form>
  </div>
</template>

<script>
import VisualizationAxes from "@/components/VisualizationAxes";
import { getSitePromise } from "../vuex/modules/site";

export default {
  name: 'VisualizationForm',

  components: {
    VisualizationAxes,
  },

  props: {
    value: Object,
    site: Object,
    clientSites: Array,
    childIdsToName: Object,
  },

  data() {
    let events = [];
    if (this.site.events && this.site.events.names) {
      events = this.site.events.names;
    }
    return {
      events,
      availableMesures: [],
      globalChartTypes: [
        { text: "Standard (Axes)", value: "BarChart" },
        { text: "Histogramme (empilé)", value: "HistogramChart" },
        { text: "Area", value: "AreaChart" },
        { text: "Donut", value: "DonutChart" },
        { text: "Pie", value: "PieChart" },
        { text: "Polar Area", value: "PolarAreaChart" },
        { text: "Scatter", value: "ScatterChart" },
        { text: "Bubble", value: "BubbleChart" },
      ],
    };
  },

  computed: {
    visualization() {
      return this.value;
    },
    annotationZones() {
      return Array.isArray(this.visualization?.annotations) ? this.visualization.annotations : [];
    },
    hasAnnotationZones() {
      return this.annotationZones.length > 0;
    },

    totalRatio() {
      if (!this.hasAnnotationZones) {
        return 0;
      }
      return this.annotationZones.reduce((sum, zone) => sum + (parseFloat(zone.ratio) || 0), 0);
    },
  },

  async mounted() {
    this.availableMesures = await this.getAvailableMesures();

    //console.log(this.visualization);

    if (!this.visualization.axes) {
      this.$set(this.visualization, 'axes', { axes: [] });
    }

    if (!this.visualization.component) {
      this.$set(this.visualization, 'component', 'BarChart');
    } else {
      const validComponents = this.globalChartTypes.map((item) => item.value);
      if (!validComponents.includes(this.visualization.component)) {
        this.$set(this.visualization, 'component', 'BarChart');
      }
    }

    if (typeof this.visualization.annotations === 'string') {
      try {
        const parsed = JSON.parse(this.visualization.annotations);
        if (Array.isArray(parsed)) {
          this.$set(this.visualization, 'annotations', parsed);
        }
      } catch (e) {
        // Ignore invalid annotation payloads to avoid mutating non-array values.
      }
    }

    this.initializeRatios();
  },

  methods: {
    hexToRgba(hex, alpha) {
      if (!hex) return 'rgba(0,0,0,0)';
      hex = hex.replace('#', '');
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    getAnnotationsArray() {
      return Array.isArray(this.visualization?.annotations) ? this.visualization.annotations : null;
    },

    initializeRatios() {
      const zones = this.getAnnotationsArray();
      if (!zones || zones.length === 0) return;

      const hasRatios = zones.some(z => z.ratio !== undefined && z.ratio !== null);

      if (!hasRatios) {
        const equalRatio = 100 / zones.length;
        zones.forEach(zone => {
          this.$set(zone, 'ratio', parseFloat(equalRatio.toFixed(1)));
        });
      } else {
        zones.forEach(zone => {
          if (zone.ratio === undefined || zone.ratio === null) {
            this.$set(zone, 'ratio', 0);
          }
        });
      }
    },

    updateRatio(changedIndex, newValue) {
      const zones = this.getAnnotationsArray();
      if (!zones || zones.length < 2) {
        if (zones && zones.length === 1) {
          this.$set(zones[0], 'ratio', 100);
        }
        return;
      }

      const newRatio = Math.max(1, Math.min(99, parseFloat(newValue) || 1));
      this.$set(zones[changedIndex], 'ratio', newRatio);

      const otherZones = zones.filter((_, i) => i !== changedIndex);
      const otherTotal = otherZones.reduce((sum, z) => sum + (z.ratio || 0), 0);

      if (otherTotal === 0) {
        const remaining = 100 - newRatio;
        const perZone = remaining / otherZones.length;
        zones.forEach((zone, i) => {
          if (i !== changedIndex) {
            this.$set(zone, 'ratio', parseFloat(perZone.toFixed(1)));
          }
        });
      } else {
        const remaining = 100 - newRatio;
        const scaleFactor = remaining / otherTotal;

        zones.forEach((zone, i) => {
          if (i !== changedIndex) {
            const adjustedRatio = Math.max(1, (zone.ratio || 0) * scaleFactor);
            this.$set(zone, 'ratio', parseFloat(adjustedRatio.toFixed(1)));
          }
        });

        this.fixRoundingErrors(changedIndex);
      }
    },

    fixRoundingErrors(lockedIndex) {
      const zones = this.getAnnotationsArray();
      if (!zones || zones.length === 0) return;
      const total = zones.reduce((sum, z) => sum + (z.ratio || 0), 0);
      const diff = 100 - total;

      if (Math.abs(diff) > 0.01) {
        let maxIndex = -1;
        let maxRatio = 0;
        zones.forEach((zone, i) => {
          if (i !== lockedIndex && zone.ratio > maxRatio) {
            maxRatio = zone.ratio;
            maxIndex = i;
          }
        });

        if (maxIndex >= 0) {
          const newRatio = zones[maxIndex].ratio + diff;
          this.$set(zones[maxIndex], 'ratio', parseFloat(Math.max(1, newRatio).toFixed(1)));
        }
      }
    },

    equalizeRatios() {
      const zones = this.getAnnotationsArray();
      if (!zones || zones.length === 0) return;

      const equalRatio = 100 / zones.length;
      zones.forEach(zone => {
        this.$set(zone, 'ratio', parseFloat(equalRatio.toFixed(1)));
      });

      this.fixRoundingErrors(-1);
    },

    normalizeRatios() {
      const zones = this.getAnnotationsArray();
      if (!zones || zones.length === 0) return;

      const total = zones.reduce((sum, z) => sum + (z.ratio || 0), 0);
      if (total === 0) {
        this.equalizeRatios();
        return;
      }

      const scaleFactor = 100 / total;
      zones.forEach(zone => {
        const newRatio = Math.max(1, (zone.ratio || 0) * scaleFactor);
        this.$set(zone, 'ratio', parseFloat(newRatio.toFixed(1)));
      });

      this.fixRoundingErrors(-1);
    },

    async getChildSite(child_id) {
      let childSite;
      const filteredSites = this.clientSites.filter((s) => s.id == child_id);

      if (filteredSites.length === 1) {
        childSite = filteredSites[0];
      } else if (filteredSites.length === 0 && this.$store.state.profile.user.role === "admin") {
        childSite = await getSitePromise(this.$store, child_id);
      }

      return childSite;
    },

    async getAvailableMesures() {
      let availableMesures = [];

      if (this.site.sensors) {
        availableMesures = [...this.site.sensors];

        if (this.childIdsToName) {
          for (const child of Object.keys(this.childIdsToName)) {
            const childSite = await this.getChildSite(child);
            if (childSite && childSite.sensors) {
              availableMesures = [...availableMesures, ...childSite.sensors];
            }
          }
        }
      }

      return availableMesures;
    },

    addAnnotationZone() {
      if (!Array.isArray(this.visualization.annotations)) {
        this.$set(this.visualization, 'annotations', []);
      }

      const zones = this.getAnnotationsArray();
      if (!zones) return;
      const numZones = zones.length + 1;
      const newRatio = 100 / numZones;

      zones.forEach(zone => {
        const adjustedRatio = (zone.ratio || 100) * (zones.length / numZones);
        this.$set(zone, 'ratio', parseFloat(adjustedRatio.toFixed(1)));
      });

      zones.push({
        text: 'Nouvelle zone',
        color: this.getNextColor(zones.length),
        ratio: parseFloat(newRatio.toFixed(1))
      });

      this.fixRoundingErrors(-1);
    },

    getNextColor(index) {
      const colors = [
        '#65E9AB', '#5B9BD5', '#FF6B6B', '#FFE66D',
        '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA',
        '#A8D8EA', '#FCBAD3', '#B5EAD7', '#C9CBA3'
      ];
      return colors[index % colors.length];
    },

    removeAnnotationZone(index) {
      if (confirm("Êtes-vous sûr de vouloir supprimer cette zone ?")) {
        const zones = this.getAnnotationsArray();
        if (!zones || zones.length === 0) return;
        const removedRatio = zones[index].ratio || 0;

        zones.splice(index, 1);

        if (zones.length > 0) {
          const addPerZone = removedRatio / zones.length;
          zones.forEach(zone => {
            this.$set(zone, 'ratio', parseFloat((zone.ratio + addPerZone).toFixed(1)));
          });
          this.fixRoundingErrors(-1);
        }
      }
    },

    moveZoneUp(index) {
      if (index > 0) {
        const zones = this.getAnnotationsArray();
        if (!zones) return;
        const temp = zones[index];
        this.$set(zones, index, zones[index - 1]);
        this.$set(zones, index - 1, temp);
      }
    },

    moveZoneDown(index) {
      const zones = this.getAnnotationsArray();
      if (!zones) return;
      if (index < zones.length - 1) {
        const temp = zones[index];
        this.$set(zones, index, zones[index + 1]);
        this.$set(zones, index + 1, temp);
      }
    },

    handleAddedAxe(axe) {
      this.$forceUpdate();
    },

    submit() {
      if (this.$refs.form.validate()) {
        this.save();
      }
    },

    save() {
      const annotationsPayload = Array.isArray(this.visualization.annotations)
        ? JSON.stringify(this.visualization.annotations)
        : this.visualization.annotations;

      const saveData = {
        ...this.visualization,
        annotations: annotationsPayload
      };

      if (this.visualization.id) {
        this.$store
            .dispatch("visualizationEditRequest", {
              id: this.visualization.id,
              datas: saveData,
            })
            .then(() => {
              this.$store.commit("setSnack", {
                type: "success",
                msg: "Visualisation enregistrée avec succès",
              });
              this.$emit("updated", this.visualization);
            })
            .catch((err) => {
              console.error("Error saving visualization:", err);
              this.$store.commit("setSnack", { type: "error", msg: err });
            });
      } else {
        this.$store
            .dispatch("visualizationCreateRequest", {
              ...saveData,
              site_id: this.site.id,
            })
            .then(() => {
              this.$store.commit("setSnack", {
                type: "success",
                msg: "Visualisation créée avec succès",
              });
              this.$emit("created", this.visualization);
            })
            .catch((err) => {
              console.error("Error creating visualization:", err);
              this.$store.commit("setSnack", { type: "error", msg: err });
            });
      }
    },

    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.visualization-form-container {
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-dark: #1e3d29;
  --color-primary-bg: #e8f0eb;

  --color-accent: #5a9e6f;
  --color-accent-light: #a8d4b8;

  --color-surface: #ffffff;
  --color-surface-secondary: #f8faf9;
  --color-surface-tertiary: #f0f4f2;
  --color-surface-hover: #eef2ef;
  --color-background: #f3f6f4;

  --color-border: #dce5df;
  --color-border-light: #e8eeea;

  --color-text-primary: #1a2e1f;
  --color-text-secondary: #4a5d4f;
  --color-text-muted: #7a8d7f;

  --color-danger: #dc2626;
  --color-danger-bg: #fef2f2;
  --color-warning: #d97706;
  --color-warning-bg: #fffbeb;
  --color-success: #059669;
  --color-success-bg: #ecfdf5;

  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.12);

  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;

  --transition-fast: 150ms ease;
  --transition-normal: 200ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  /*max-width: 1000px;*/
  width: 100%;
  overflow: hidden;
}

/* ============================================
   HEADER
   ============================================ */
.form-header {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  padding: 24px 28px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon .material-symbols-outlined {
  font-size: 26px;
  color: white;
}

.header-info h1 {
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.header-info p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 4px 0 0;
}

.header-info .placeholder-text {
  font-style: italic;
  opacity: 0.7;
}

/* ============================================
   FORM BODY
   ============================================ */
.form-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-height: 70vh;
  overflow-y: auto;
}

/* ============================================
   SECTIONS
   ============================================ */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border-light);
}

.section-header .material-symbols-outlined {
  font-size: 20px;
  color: var(--color-accent);
}

.section-header h2 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  flex: 1;
}

.section-badge {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-primary);
  background: var(--color-primary-bg);
  padding: 4px 10px;
  border-radius: 20px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* ============================================
   FIELDS
   ============================================ */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.field-label.required::after {
  content: ' *';
  color: var(--color-danger);
}

.field-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  background: var(--color-surface);
  transition: var(--transition-normal);
  font-family: inherit;
}

.form-input:hover {
  border-color: var(--color-primary-lighter);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.form-input::placeholder {
  color: var(--color-text-muted);
}

/* ============================================
   RATIO BADGE
   ============================================ */
.ratio-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.ratio-badge.valid {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.ratio-badge.warning {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

.ratio-badge .material-symbols-outlined {
  font-size: 14px;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-annotations {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  background: var(--color-surface-secondary);
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-md);
}

.empty-annotations .material-symbols-outlined {
  font-size: 40px;
  color: var(--color-text-muted);
  opacity: 0.4;
  margin-bottom: 12px;
}

.empty-annotations h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

.empty-annotations p {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 0 0 16px;
}

/* ============================================
   ANNOTATIONS LIST
   ============================================ */
.annotations-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.annotation-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  transition: var(--transition-normal);
}

.annotation-card:hover {
  border-color: var(--color-border);
  box-shadow: var(--shadow-sm);
}

/* Order Controls */
.annotation-order {
  flex-shrink: 0;
}

.order-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.order-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 4px;
  cursor: pointer;
  transition: var(--transition-fast);
}

.order-btn:hover:not(:disabled) {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.order-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.order-btn .material-symbols-outlined {
  font-size: 18px;
}

.order-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  width: 20px;
  text-align: center;
}

/* Annotation Content */
.annotation-content {
  flex: 1;
  display: grid;
  grid-template-columns: auto 1fr 100px 80px;
  gap: 12px;
  align-items: center;
}

@media (max-width: 700px) {
  .annotation-content {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

/* Color Field */
.color-field {
  flex-shrink: 0;
}

.color-picker-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-normal);
}

.color-picker-trigger:hover {
  border-color: var(--color-primary-lighter);
}

.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.color-hex {
  font-size: 0.75rem;
  font-family: 'SF Mono', monospace;
  color: var(--color-text-secondary);
}

.color-picker-trigger .material-symbols-outlined {
  font-size: 16px;
  color: var(--color-text-muted);
}

.color-picker-popup {
  background: var(--color-surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

/* Text Field */
.text-field {
  flex: 1;
}

.text-field .form-input {
  padding: 10px 12px;
  font-size: 0.875rem;
}

/* Ratio Field */
.ratio-field {
  position: relative;
  display: flex;
  align-items: center;
}

.ratio-input {
  padding-right: 28px !important;
  text-align: right;
  font-family: 'SF Mono', monospace;
  font-size: 0.875rem !important;
}

.ratio-suffix {
  position: absolute;
  right: 12px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  pointer-events: none;
}

/* Preview Bar */
.ratio-preview-bar {
  width: 80px;
  height: 8px;
  background: var(--color-surface-tertiary);
  border-radius: 4px;
  overflow: hidden;
}

.preview-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Delete Button */
.annotation-delete {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.annotation-delete:hover {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.annotation-delete .material-symbols-outlined {
  font-size: 20px;
}

/* ============================================
   ANNOTATIONS PREVIEW
   ============================================ */
.annotations-preview {
  margin-top: 16px;
  padding: 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.preview-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
}

.preview-label .material-symbols-outlined {
  font-size: 16px;
}

.preview-chart {
  height: 180px;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-zone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-left: 4px solid;
  min-height: 24px;
  transition: height 0.3s ease;
}

.preview-zone-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-zone-ratio {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-text-muted);
  font-family: 'SF Mono', monospace;
}

/* ============================================
   ANNOTATIONS ACTIONS
   ============================================ */
.annotations-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-sm {
  padding: 8px 14px;
  font-size: 0.8125rem;
}

.btn-sm .material-symbols-outlined {
  font-size: 16px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-light);
}

.btn-secondary {
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover {
  background: var(--color-surface-hover);
  border-color: var(--color-primary-lighter);
}

.btn-ghost {
  background: transparent;
  color: var(--color-text-secondary);
}

.btn-ghost:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

/* ============================================
   FOOTER
   ============================================ */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px;
  background: var(--color-surface-secondary);
  border-top: 1px solid var(--color-border-light);
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.custom-autocomplete >>> .v-input__slot {
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 46px !important;
}

.custom-autocomplete >>> .v-input__slot:hover {
  border-color: var(--color-primary-lighter) !important;
}

.custom-autocomplete >>> .v-input--is-focused .v-input__slot {
  border-color: var(--color-primary) !important;
}

.custom-autocomplete >>> .v-chip {
  background: var(--color-primary) !important;
  color: white !important;
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>
